import { useState } from 'react';
import { clsx } from 'clsx';
import { Sparkles, ChevronDown, ChevronUp, Copy, Check, Lightbulb, Loader2 } from 'lucide-react';
import type { SuggestionItem, ResponseTone } from '../../../services/ai.service';

const TONES: Record<ResponseTone, { label: string; icon: string }> = {
  professional: { label: 'Professional', icon: '💼' },
  empathetic: { label: 'Empathetic', icon: '💚' },
  apologetic: { label: 'Apologetic', icon: '🙏' },
  enthusiastic: { label: 'Enthusiastic', icon: '🎉' },
  technical: { label: 'Technical', icon: '🔧' },
};

export interface AISuggestionsPanelProps {
  suggestions: SuggestionItem[];
  isLoading?: boolean;
  error?: string;
  selectedTone?: ResponseTone;
  /** Regenerate, optionally in a new tone. */
  onRefresh?: (tone?: ResponseTone) => void;
  /** Put the suggestion into the reply composer. */
  onUse?: (suggestion: SuggestionItem) => void;
  /** Hides the Use button (e.g. resolved/closed tickets can't be replied to). */
  canUse?: boolean;
}

/**
 * AI reply suggestions for the ticket header card: collapsible header with a
 * count, tone chips that regenerate, and full suggestion cards with copy / use.
 */
export function AISuggestionsPanel({
  suggestions,
  isLoading,
  error,
  selectedTone,
  onRefresh,
  onUse,
  canUse = true,
}: AISuggestionsPanelProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleCopy = async (content: string, index: number) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch {
      /* clipboard unavailable — nothing to do */
    }
  };

  return (
    <div className="overflow-hidden rounded-[12px] border border-[var(--co-line)] bg-[var(--co-surface)]">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-2 bg-[var(--co-brass-wash)] px-4 py-2.5 text-left"
      >
        <span className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[var(--co-brass-ink)]" />
          <span className="text-[14px] font-semibold text-[var(--co-ink)]">AI Suggestions</span>
          {!isLoading && suggestions.length > 0 && (
            <span className="rounded-full bg-[var(--co-raised)] px-2 py-0.5 text-[11px] font-semibold text-[var(--co-brass-ink)]">
              {suggestions.length}
            </span>
          )}
          {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--co-ink-4)]" />}
        </span>
        {isOpen ? (
          <ChevronUp className="h-4 w-4 text-[var(--co-ink-4)]" />
        ) : (
          <ChevronDown className="h-4 w-4 text-[var(--co-ink-4)]" />
        )}
      </button>

      {isOpen && (
        <div className="space-y-3 p-3">
          {onRefresh && (
            <div className="flex flex-wrap gap-1.5">
              {(Object.keys(TONES) as ResponseTone[]).map((tone) => (
                <button
                  key={tone}
                  type="button"
                  disabled={isLoading}
                  onClick={() => onRefresh(tone)}
                  className={clsx(
                    'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-medium transition-colors duration-[120ms] disabled:opacity-60',
                    selectedTone === tone
                      ? 'bg-[var(--co-brass-wash)] text-[var(--co-brass-ink)] ring-1 ring-[var(--co-brass)]'
                      : 'text-[var(--co-ink-3)] hover:bg-[var(--co-canvas)]',
                  )}
                >
                  <span>{TONES[tone].icon}</span>
                  <span>{TONES[tone].label}</span>
                </button>
              ))}
            </div>
          )}

          {error ? (
            <div className="flex items-center justify-between gap-3 rounded-[10px] border border-[var(--co-burgundy)] px-3 py-2.5">
              <span className="text-[13px] text-[var(--co-burgundy)]">AI suggestions unavailable</span>
              {onRefresh && (
                <button
                  type="button"
                  onClick={() => onRefresh()}
                  className="rounded-full px-2.5 py-1 text-[12px] font-medium text-[var(--co-ink-2)] hover:bg-[var(--co-canvas)]"
                >
                  Try again
                </button>
              )}
            </div>
          ) : isLoading ? (
            <div className="space-y-2">
              {[0, 1].map((i) => (
                <div
                  key={i}
                  aria-hidden="true"
                  className="h-[88px] animate-pulse rounded-[10px] border border-[var(--co-line-subtle)] bg-[var(--co-canvas)]"
                />
              ))}
            </div>
          ) : suggestions.length === 0 ? (
            <div className="py-4 text-center text-[var(--co-ink-4)]">
              <Lightbulb className="mx-auto mb-2 h-7 w-7 opacity-50" />
              <p className="text-sm">No suggestions available</p>
              {onRefresh && (
                <button
                  type="button"
                  onClick={() => onRefresh()}
                  className="mt-2 rounded-full px-3 py-1 text-[12px] font-medium text-[var(--co-brass-ink)] hover:bg-[var(--co-brass-wash)]"
                >
                  Generate suggestions
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className="group rounded-[10px] border border-[var(--co-line)] bg-[var(--co-raised)] p-3 transition-colors duration-[120ms] hover:border-[var(--co-brass)]"
                >
                  <p className="whitespace-pre-wrap text-[13.5px] leading-[1.55] text-[var(--co-ink)]">
                    {suggestion.content}
                  </p>
                  <div className="mt-2.5 flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2">
                      {suggestion.tone && (
                        <span className="rounded-full border border-[var(--co-line)] px-2.5 py-0.5 text-[12px] font-medium capitalize text-[var(--co-ink-2)]">
                          {suggestion.tone}
                        </span>
                      )}
                      <span className="text-[12px] text-[var(--co-ink-4)]">
                        {Math.round((suggestion.confidence ?? 0) * 100)}% match
                      </span>
                    </span>
                    <span className="flex items-center gap-1 opacity-0 transition-opacity duration-[120ms] focus-within:opacity-100 group-hover:opacity-100">
                      <button
                        type="button"
                        onClick={() => handleCopy(suggestion.content, index)}
                        title="Copy to clipboard"
                        className="flex h-7 w-7 items-center justify-center rounded-full text-[var(--co-ink-3)] hover:bg-[var(--co-canvas)]"
                      >
                        {copiedIndex === index ? (
                          <Check className="h-3.5 w-3.5 text-[var(--co-green)]" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </button>
                      {onUse && canUse && (
                        <button
                          type="button"
                          onClick={() => onUse(suggestion)}
                          className="rounded-full bg-[var(--co-brass)] px-3 py-1 text-[12px] font-semibold text-[var(--co-brass-on)] hover:bg-[var(--co-brass-hover)]"
                        >
                          Use
                        </button>
                      )}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AISuggestionsPanel;
